import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { LoadableDataState } from "@my-dld/utils/UIUtils";
import React, { Component } from "react";
import { TenantContractContext, updateTenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import { defaultErrorManager } from "@eres/forms/errormanager";
import FormSection from "@my-dld/components/Form/FormSection";
import Strings from "@my-dld/assets/strings";
import TextInputField from "@eres/forms-web/TextInputField";
import { dualDataManager } from "@eres/forms/valuemanager/DualDataManager";
import FormRow from "@eres/forms-web/FormRow";
import { TCTerminateInfo } from "@eres/ejari-api/Entity/tenant";
import { Lookup } from "@eres/ejari-api/Entity";
import { populateLookupOnTo, StateConsumer, TerminationReasonTransformer } from "@eres/ejari-api/Utils";
import { EjariLookUp } from "@eres/ejari-api/constants";
import { IdExtractor, lookupConverter, serverDateConverter } from "@my-dld/utils/LookupUtil";
import PickerInput from "@eres/forms-web/PickerInput";
import _ from "lodash";
import DateInputField from "@eres/forms-web/DateInputField";
import FormAction from "@my-dld/components/Form/FormAction";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "../../utils";
import { isEmailAddress } from "@eres/core/dist/StringUtils";
import moment from "moment";
import { navigate } from "@my-dld/utils/StoreUtils";
import FormMandatory from "../FormMandatory";

interface Props extends RoutedViewProps {

}

interface LookupState {
	Reasons?: Lookup[];
}

interface State extends LoadableDataState, LookupState {
	newData?: TCTerminateInfo;
	orgData?: TCTerminateInfo;
	maxTerminateDate?: Date;
	minTerminateDate?: Date;
}

class TerminateInfo extends Component<Props, State> {
	static contextType = TenantContractContext;
	context!: React.ContextType<typeof TenantContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			errorContainer: {},
			loadState: "loading",
		};
	}

	componentDidMount(): void {
		const { terminateInfo, contract } = this.context;
		const state = {
			orgData: terminateInfo || {},
			newData: terminateInfo || {},
		};
		const data: TCTerminateInfo = {};
		_.merge( data, terminateInfo );
		let maxDate, minDate;
		if ( contract ) {
			maxDate = contract.ContractEndDate!.asDate();
			minDate = contract.ContractStartDate!.asDate();
			let today = moment( new Date() );
			let sd = moment( minDate );
			let ed = moment( maxDate );
			if ( sd.format( "MM-dd-yyyy" ) === "01-01-0001" ) {
				minDate = undefined;
			}
			if ( ed.format( "MM-dd-yyyy" ) === "01-01-0001" ) {
				maxDate = undefined;
			}
			if ( maxDate && minDate && terminateInfo == undefined ) {
				if ( today.isAfter( minDate ) && today.isBefore( maxDate ) ) {
					state.newData.TerminationDate = today.toDate().asServerString();
				} else if ( today.isBefore( minDate ) ) {
					state.newData.TerminationDate = sd.toDate().asServerString();
				} else if ( today.isAfter( maxDate ) ) {
					state.newData.TerminationDate = ed.toDate().asServerString();
				}
			}
		}
		this.setState( {
			...state,
			maxTerminateDate: maxDate,
			minTerminateDate: minDate,
		} );
		populateLookupOnTo( EjariLookUp.TERMINATE_REASONS, StateConsumer( this, "Reasons" ), TerminationReasonTransformer );
	}

	render() {
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
			valueManager: dualDataManager,
			errorManager: defaultErrorManager,
		};
		const { Reasons, maxTerminateDate, minTerminateDate } = this.state;
		const data = this.getUpdatedData();
		return <div className="tender-registration-form">
			<FormSection title={ Strings.terminate_contract }>
				<FormMandatory/>
				<FormRow { ...filedProps }>
					<TextInputField
						required={ true }
						id={ "RequesterEmail" }
						label={ Strings.email }/>
					<DateInputField
						required={ true }
						id={ "TerminationDate" }
						label={ Strings.termination_date }
						containerClassName={ "field-style select-control" }
						valueConverter={ serverDateConverter }
						extraOpts={ {
							maxDate: maxTerminateDate,
							minDate: minTerminateDate,
						} }/>
					<PickerInput
						required={ true }
						id={ "TerminationReasonId" }
						label={ Strings.termination_reason }
						isLoading={ Reasons === undefined }
						items={ Reasons }
						toIdentifier={ IdExtractor }
						valueConverter={ lookupConverter }/>
					{ data.TerminationReasonId === -1 && <TextInputField
						required={ true }
						id={ "TerminationReasonText" }
						label={ Strings.termination_reason }/> }
				</FormRow>
				<FormRow>
					<FormAction
						primaryActionLabel={ Strings.continue }
						onPrimaryAction={ this.onContinue }
						secondaryActionLabel={ Strings.back }
						onSecondaryAction={ this.onBack }/>
				</FormRow>
			</FormSection>
		</div>;
	}

	private getUpdatedData = ( state: State = this.state ) => {
		const data: TCTerminateInfo = {};
		_.merge( data, state.orgData, state.newData );
		return data;
	};

	private isValid() {
		const data = this.getUpdatedData();
		const error: ErrorContainer = {};
		let isValid = checkNonEmpty( data, "RequesterEmail", error );
		if ( data.RequesterEmail && !isEmailAddress( data.RequesterEmail ) ) {
			isValid = false;
			error.RequesterEmail = Strings.email_validation;
		}
		isValid = checkNonEmpty( data, "TerminationDate", error ) && isValid;
		if ( data.TerminationReasonId == undefined ) {
			isValid = false;
			error.TerminationReasonId = Strings.required;
		}
		if ( data.TerminationReasonId && data.TerminationReasonId === -1 ) {
			isValid = checkNonEmpty( data, "TerminationReasonText", error ) && isValid;
		}
		this.setState( {
			errorContainer: error,
		} );
		return isValid;
	}

	private onContinue = () => {
		if ( this.isValid() ) {
			const data = this.getUpdatedData();
			updateTenantContractContext( {
				terminateInfo: data,
			} );
			navigate( "documents" );
		}
	};

	private onBack = () => {
		this.props.history.go( -2 );
	};
}

export default TerminateInfo;
